/**
 * PropiedadFinancePanel — panel "Configuración financiera" del Modal
 * de propiedad. Extraído de propiedades.tsx en round 2026-04-27 (Fase 8).
 *
 * QUÉ HACE
 *   1. Select "Plan" — catálogo de planes (config-options) con creación
 *      inline desde el dropdown (alta vía configOptionApi).
 *   2. InputNumber "Comisión de la propiedad (%)" — 0..100, step 0.1.
 *   3. Card "Desglose de limpieza" — 4 subitems que componen la tarifa
 *      cobrada al huésped:
 *        1) Pago aseador (sin IVA — persona natural).
 *        2) Transporte (neto / IVA / total — autocálculo bidireccional).
 *        3) Materiales  (idem).
 *        4) Lavandería  (idem).
 *      Total siempre derivado (suma de los 4).
 *   4. Select "Lavandería" — asocia una lavandería AF a la propiedad.
 *
 * AUTO-CÁLCULO IVA (bidireccional, ver docs/modulo-arriendos-precios.md):
 *   - Editar NETO  → IVA = round(neto * 0.19), TOTAL = neto + IVA.
 *   - Editar TOTAL → NETO = round(total / 1.19), IVA = total - NETO.
 *   - El IVA siempre es read-only (calculado, no editable).
 *
 * Verificador: si neto + IVA ≠ total en algún subitem (datos legacy
 * inconsistentes), muestra warning amarillo. Al guardar persiste los
 * valores tal cual están — no fuerza recálculo.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El bloque tenía ~470 LOC de Form.Items + Card + Cards anidadas
 *     con dropdownRender de creación de plan.
 *   - Recibe handlers/deltas/sums calculados en el padre (porque
 *     viven dentro del mismo Modal scope con `Form.useWatch`).
 *   - Aislar el panel facilita testing visual y evita que el padre
 *     tenga que renderizar 470 LOC de JSX cada vez que cambia algún
 *     watch del Modal.
 */

import React from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import { configOptionApi } from "../../../services/configOption.api";
import { clpInputFormatter, clpInputParser } from "../../../utils/clpInput";
import { getIvaPorcentaje } from "../../../utils/iva";

const { Text } = Typography;

// ─── Tipos auxiliares ───────────────────────────────────────────────────

/** Prefix usado por los handlers de neto/total para identificar el
 *  subitem (mismo enum que el padre — mantenemos compat exacta). */
type SubitemPrefix = "Transporte" | "Materiales" | "Lavanderia";

/** Forma genérica de una lavandería para el Select. */
interface LavanderiaOpt {
  id: number;
  nombre: string;
  activo: boolean;
  // Marca la fila "Lavandería externa" del sistema, default obligatorio
  // cuando la propiedad no tiene una AF interna. La UI la etiqueta como
  // "Sistema" en el dropdown para diferenciarla del resto.
  es_generica?: boolean;
}

interface PropiedadFinancePanelProps {
  /** Form instance del Modal padre. */
  form: FormInstance;

  /** Catálogo de planes (strings) — owner del padre. */
  planOptions: string[];
  setPlanOptions: React.Dispatch<React.SetStateAction<string[]>>;
  /** Ref al Select de planes — para `.blur()` tras crear plan nuevo
   *  y cerrar el dropdown automáticamente. */
  planSelectRef: React.MutableRefObject<any>;

  /** Catálogo de lavanderías AF disponibles para asociar. */
  lavanderiaOptions: LavanderiaOpt[];

  /** Handlers de auto-cálculo neto↔total. Owner del padre porque
   *  necesitan acceder al `form` y otros campos para sincronizar. */
  handleNetoChange: (prefix: SubitemPrefix, rawValue: unknown) => void;
  handleTotalChange: (prefix: SubitemPrefix, rawValue: unknown) => void;

  /** Deltas |neto + IVA - total| de cada subitem — para mostrar warnings
   *  cuando hay inconsistencia (típicamente datos legacy). */
  deltaTransporte: number;
  deltaMateriales: number;
  deltaLavanderia: number;

  /** Suma de los 4 subitems (pago aseador + 3 totales) — el total cobrado
   *  al huésped. Read-only en UI pero se persiste en `precioLimpiezaCobrado`. */
  sumaSubitems: number;
  /** True si CUALQUIER subitem tiene delta > 1 — muestra Alert amarilla
   *  + cambia color del Card de total. */
  hasDeltaSubitems: boolean;
}

// ─── Componente ─────────────────────────────────────────────────────────

const PropiedadFinancePanel: React.FC<PropiedadFinancePanelProps> = ({
  form,
  planOptions,
  setPlanOptions,
  planSelectRef,
  lavanderiaOptions,
  handleNetoChange,
  handleTotalChange,
  deltaTransporte,
  deltaMateriales,
  deltaLavanderia,
  sumaSubitems,
  hasDeltaSubitems,
}) => {
  return (
    <div className="flex-container">
      <Form.Item
        label="Plan"
        name="plan"
        tooltip="El plan es solo una etiqueta de referencia (ej. «Plan Estándar 18»). NO fija la comisión: la comisión se define aparte y puede no calzar con el plan."
      >
        <Select
          ref={planSelectRef}
          allowClear
          showSearch
          optionFilterProp="label"
          options={planOptions.map((p) => ({
            value: p,
            label: p,
          }))}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <div
                style={{
                  padding: "4px 12px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  Crear nuevo plan
                </span>
                <Input
                  placeholder="Nombre del plan"
                  id="new-plan-name"
                  size="small"
                />
                <Input.TextArea
                  placeholder="Detalle o descripción (opcional)"
                  id="new-plan-desc"
                  autoSize={{ minRows: 2, maxRows: 3 }}
                  style={{ fontSize: 13 }}
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  block
                  onClick={async () => {
                    const nameEl = document.getElementById(
                      "new-plan-name",
                    ) as HTMLInputElement;
                    const descEl = document.getElementById(
                      "new-plan-desc",
                    ) as HTMLTextAreaElement;
                    const nombre = nameEl?.value.trim();
                    const desc = descEl?.value.trim();
                    if (!nombre) return;
                    await configOptionApi.create({
                      categoria: "plan",
                      valor: nombre,
                      ...(desc ? { descripcion: desc } : {}),
                    } as any);
                    setPlanOptions((prev) => [...prev, nombre]);
                    form.setFieldValue("plan", nombre);
                    if (nameEl) nameEl.value = "";
                    if (descEl) descEl.value = "";
                    planSelectRef.current?.blur();
                  }}
                >
                  Agregar plan
                </Button>
              </div>
            </>
          )}
        />
      </Form.Item>

      <Form.Item
        label="Comisión de la propiedad (%)"
        name="comisionPropiedad"
        tooltip="Porcentaje que cobra Aloja Fácil por la gestión de esta propiedad. Es independiente del plan: puedes fijar la que quieras (p.ej. una rebaja) aunque no calce con el plan."
      >
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          max={100}
          step={0.1}
          controls={false}
          placeholder="18"
        />
      </Form.Item>

      {/*
       * Aclaración explícita (admin/superadmin): plan y comisión son
       * INDEPENDIENTES. El plan es una etiqueta de referencia y NO obliga a
       * una comisión; el sistema SIEMPRE usa el valor de "Comisión de la
       * propiedad" para los cálculos. Esto pasa, por ejemplo, con rebajas de
       * comisión que no calzan con ningún plan. Pedido del owner para evitar
       * la duda de que el plan "fije" la comisión.
       */}
      {/*
       * Precio mínimo por noche acordado con el propietario.
       * IMPORTANTE: es el precio por noche BASE a cobrar al ARRENDATARIO (lo
       * que paga el huésped por noche), NO el monto que recibe el propietario.
       * Es el piso del precio por noche pactado con el dueño. Si al fijar el
       * precio en el calendario alguien pone un valor menor a éste, el sistema
       * avisa (confirmación + alerta administrativa) por estar pasando a llevar
       * el acuerdo. Es opcional e informativo: no bloquea, solo advierte.
       */}
      <Form.Item
        label="Precio mínimo por noche acordado (CLP)"
        name="precioMinimoNoche"
        tooltip="Precio por noche BASE mínimo acordado con el propietario para cobrar al arrendatario (lo que paga el huésped por noche, NO el monto a recibir por el propietario). Es el piso del precio por noche base. Si en el calendario se fija un precio menor, el sistema te pedirá confirmar y registrará una alerta para el admin por pasar a llevar el acuerdo. Opcional: déjalo vacío si no hay mínimo pactado."
        rules={[
          {
            type: "number",
            min: 0,
            message: "Debe ser un monto válido (≥ 0)",
          },
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          step={1000}
          controls={false}
          formatter={clpInputFormatter}
          parser={clpInputParser}
          placeholder="Ej. 45.000 (vacío = sin mínimo)"
        />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="El plan y la comisión son independientes"
        description="El plan es solo una etiqueta de referencia (ej. «Plan Estándar 18»). La comisión de arriba es la que el sistema usa para todos los cálculos y no tiene por qué calzar con el plan: puedes fijar una rebaja u otro valor sin cambiar el plan."
      />

      {/*
       * ═══ DESGLOSE DE LIMPIEZA ═══
       * Agrupa los 4 subitems que componen la tarifa cobrada al huésped
       * en un Card con hierarchy visual clara:
       *   1. Pago aseador (sin IVA — persona natural)
       *   2. Transporte   (neto + IVA + total)
       *   3. Materiales   (neto + IVA + total)
       *   4. Lavandería   (neto + IVA + total)
       * Total = (1) + totales de (2..4) — SIEMPRE derivado.
       *
       * Para los subitems con IVA sólo se editan NETO o TOTAL. El IVA
       * (19 %) se deriva y se muestra read-only. Al tocar neto →
       * iva=round(neto*0.19), total=neto+iva. Al tocar total →
       * neto=round(total/1.19), iva=total-neto.
       *
       * Verificador: alerta si total ≠ neto + iva en algún subitem
       * (útil sólo para datos legacy inconsistentes).
       *
       * Ver docs/modulo-arriendos-precios.md §Desglose de limpieza.
       */}
      <Card
        size="small"
        title={
          <Space size={6}>
            <Text strong>Desglose de limpieza</Text>
            <Tooltip
              title={
                <span>
                  La tarifa de limpieza cobrada al huésped se descompone
                  en 4 subitems:
                  <br />• <b>Pago aseador</b> — al aseador (sin IVA).
                  <br />• <b>Transporte</b> — total va a AF empresa (el
                  neto alimenta cálculos financieros posteriores).
                  <br />• <b>Materiales</b> — total va a AF empresa (el
                  neto alimenta cálculos financieros posteriores).
                  <br />• <b>Lavandería</b> — total va a la lavandería AF
                  si hay una asociada; si no, a terceros.
                  <br />
                  <br />
                  Para los 3 subitems con IVA sólo editas <b>neto</b> o{" "}
                  <b>total</b>; el <b>IVA</b> ({getIvaPorcentaje()} %, tasa
                  configurable en /configuracion → Precios e IVA) se calcula
                  solo y queda read-only.
                  <br />
                  <br />
                  El <b>total limpieza</b> es siempre la suma — no
                  editable.
                </span>
              }
            >
              <InfoCircleOutlined style={{ opacity: 0.6 }} />
            </Tooltip>
          </Space>
        }
        // gridColumn: "1 / -1" rompe el layout CSS-grid de
        // .flex-container (repeat(auto-fill, minmax(220px, 1fr))) para
        // que el Card ocupe la fila completa en vez de quedar comprimido
        // en una sola columna de ~220px.
        style={{
          gridColumn: "1 / -1",
          marginTop: 12,
          marginBottom: 12,
          background: "#fafafa",
          border: "1px solid #e5e5e5",
        }}
        styles={{ body: { padding: 14 } }}
      >
        {/* 1. Pago aseador — sin IVA */}
        <div
          style={{
            padding: "8px 10px",
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            1 · PAGO ASEADOR (sin IVA)
          </Text>
          <Form.Item
            name="precioLimpiezaAseador"
            style={{ marginTop: 4, marginBottom: 0 }}
            tooltip="Lo que AF paga al aseador por limpiar esta propiedad. No lleva IVA (el aseador es persona natural)."
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={1000}
              formatter={clpInputFormatter}
              parser={clpInputParser}
              placeholder="Ej: 15.000"
            />
          </Form.Item>
        </div>

        {/*
         * Subitems 2..4: estructura idéntica (neto / IVA auto / total).
         * Mantenemos los 3 inline en lugar de abstraerlos en un
         * sub-componente porque cada uno tiene su propio `name=` de
         * Form.Item y su propio delta — abstraer ahorraría ~30 LOC pero
         * añadiría 6 props por instancia y un nivel de indirección.
         */}

        {/* 2. Transporte — neto / IVA / total */}
        <div
          style={{
            padding: "8px 10px",
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            2 · TRANSPORTE{" "}
            <Text type="secondary" style={{ fontSize: 10 }}>
              (total va a AF empresa · neto para cálculos posteriores)
            </Text>
          </Text>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Form.Item
              label="Neto"
              name="precioTransporteNeto"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleNetoChange("Transporte", v)}
              />
            </Form.Item>
            <Form.Item
              label={`IVA ${getIvaPorcentaje()} % (auto)`}
              name="precioTransporteIva"
              className="af-iva-label"
              style={{ flex: 1, marginBottom: 0 }}
              tooltip="Calculado automáticamente a partir del neto o del total — no editable."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                disabled
              />
            </Form.Item>
            <Form.Item
              label="Total"
              name="precioTransporteTotal"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleTotalChange("Transporte", v)}
              />
            </Form.Item>
          </div>
          {deltaTransporte > 1 && (
            <Text type="warning" style={{ fontSize: 11 }}>
              ⚠ neto + IVA ≠ total (delta $
              {deltaTransporte.toLocaleString("es-CL")}) — toca neto o
              total para corregir
            </Text>
          )}
        </div>

        {/* 3. Materiales — neto / IVA / total */}
        <div
          style={{
            padding: "8px 10px",
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            3 · MATERIALES{" "}
            <Text type="secondary" style={{ fontSize: 10 }}>
              (total va a AF empresa · neto para cálculos posteriores)
            </Text>
          </Text>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Form.Item
              label="Neto"
              name="precioMaterialesNeto"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleNetoChange("Materiales", v)}
              />
            </Form.Item>
            <Form.Item
              label={`IVA ${getIvaPorcentaje()} % (auto)`}
              name="precioMaterialesIva"
              className="af-iva-label"
              style={{ flex: 1, marginBottom: 0 }}
              tooltip="Calculado automáticamente a partir del neto o del total — no editable."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                disabled
              />
            </Form.Item>
            <Form.Item
              label="Total"
              name="precioMaterialesTotal"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleTotalChange("Materiales", v)}
              />
            </Form.Item>
          </div>
          {deltaMateriales > 1 && (
            <Text type="warning" style={{ fontSize: 11 }}>
              ⚠ neto + IVA ≠ total (delta $
              {deltaMateriales.toLocaleString("es-CL")}) — toca neto o
              total para corregir
            </Text>
          )}
        </div>

        {/* 4. Lavandería — neto / IVA / total */}
        <div
          style={{
            padding: "8px 10px",
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            marginBottom: 10,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            4 · LAVANDERÍA{" "}
            <Text type="secondary" style={{ fontSize: 10 }}>
              (total va a lavandería AF si hay asociada; si no, a
              terceros · neto para cálculos posteriores)
            </Text>
          </Text>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Form.Item
              label="Neto"
              name="precioLavanderiaNeto"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleNetoChange("Lavanderia", v)}
              />
            </Form.Item>
            <Form.Item
              label={`IVA ${getIvaPorcentaje()} % (auto)`}
              name="precioLavanderiaIva"
              className="af-iva-label"
              style={{ flex: 1, marginBottom: 0 }}
              tooltip="Calculado automáticamente a partir del neto o del total — no editable."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                disabled
              />
            </Form.Item>
            <Form.Item
              label="Total"
              name="precioLavanderiaTotal"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={1000}
                formatter={clpInputFormatter}
              parser={clpInputParser}
                onChange={(v) => handleTotalChange("Lavanderia", v)}
              />
            </Form.Item>
          </div>
          {deltaLavanderia > 1 && (
            <Text type="warning" style={{ fontSize: 11 }}>
              ⚠ neto + IVA ≠ total (delta $
              {deltaLavanderia.toLocaleString("es-CL")}) — toca neto o
              total para corregir
            </Text>
          )}
        </div>

        {/*
         * Total limpieza — siempre derivado. El Form.Item queda oculto
         * (el valor se persiste en DB para que el cálculo histórico no
         * dependa de re-derivar al render), y mostramos un resumen
         * visual grande arriba con color contextual:
         *   - azul (#e6f4ff): consistente.
         *   - amarillo (#fffbe6): hay deltas → mostramos Alert con
         *     descripción y sugerencia.
         */}
        <Form.Item name="precioLimpiezaCobrado" hidden>
          <InputNumber />
        </Form.Item>

        <div
          style={{
            padding: "10px 12px",
            background: hasDeltaSubitems ? "#fffbe6" : "#e6f4ff",
            border: `1px solid ${
              hasDeltaSubitems ? "#ffe58f" : "#91caff"
            }`,
            borderRadius: 6,
          }}
        >
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Space direction="vertical" size={0}>
              <Text
                strong
                style={{
                  fontSize: 12,
                  color: hasDeltaSubitems ? "#ad6800" : "#0958d9",
                }}
              >
                TOTAL LIMPIEZA cobrado al huésped (incluye IVA)
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                = pago aseador + transporte total + materiales total +
                lavandería total
              </Text>
            </Space>
            <Text
              strong
              style={{
                fontSize: 20,
                color: hasDeltaSubitems ? "#ad6800" : "#002c8c",
              }}
            >
              ${Math.round(sumaSubitems).toLocaleString("es-CL")}
            </Text>
          </Space>
          {hasDeltaSubitems && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="Hay inconsistencias en los subitems"
              description={
                <span>
                  En uno o más subitems <b>neto + IVA ≠ total</b>. Esto
                  suele pasar con datos legacy cargados antes del
                  desglose de IVA. Ajusta cualquier campo del subitem
                  afectado (o déjalo como está si sabes lo que haces) —
                  al guardar se persistirán los valores mostrados.
                </span>
              }
            />
          )}
        </div>
      </Card>

      {/*
        Lavandería asociada a la propiedad — campo obligatorio.
        Regla del proyecto: TODA propiedad debe tener una lavandería. Cuando no
        hay una AF interna acordada con el dueño, se usa la "Lavandería externa"
        (es_generica=true), que es el default seteado al abrir el form de crear.
        Por eso quitamos allowClear: si el admin la "limpia", el form quedaría
        en estado inválido y el backend volvería a defaultear a la genérica.
      */}
      <Form.Item
        label="Lavandería"
        name="lavanderiaId"
        rules={[{ required: true, message: "Selecciona una lavandería" }]}
        tooltip="Toda propiedad debe tener una lavandería asociada. Si no usas una AF interna, deja la 'Lavandería externa' (default del sistema)."
      >
        <Select
          style={{ height: 32 }}
          showSearch
          optionFilterProp="label"
          placeholder="Selecciona lavandería"
          // Round 2026-05-11 iter 26: incluimos también las lavanderías
          // INACTIVAS si la propiedad ya apunta a una (sino el dropdown
          // queda mostrando "Selecciona lavandería" sin opción de mantener
          // la actual). Las marcamos con un tag "Inactiva" para que el
          // admin las reconozca y decida si reasignar.
          options={lavanderiaOptions.map((l) => ({
            value: l.id,
            // Si es la genérica, anteponemos el tag "Sistema" para que el
            // admin entienda que es el default y no una lavandería más.
            // Si está desactivada, la marcamos para que el admin lo note.
            label: `${l.es_generica ? `${l.nombre}  ·  Sistema` : l.nombre}${
              l.activo ? "" : "  ·  ⚠️ Inactiva"
            }`,
            disabled: !l.activo,
          }))}
        />
      </Form.Item>
      {/* Round 2026-05-11 iter 26: warning si la lavandería actual está
          desactivada. Aviso importante porque los lavados nuevos seguirán
          apuntando a una lavandería deshabilitada (no se filtra por activo
          en el backend) y los reportes la siguen contabilizando. Forzar
          reasignación es decisión del admin. */}
      <Form.Item noStyle shouldUpdate>
        {() => {
          const currentLavId = form.getFieldValue("lavanderiaId");
          const lav = lavanderiaOptions.find((l) => l.id === currentLavId);
          if (!lav || lav.activo) return null;
          return (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: -8, marginBottom: 12 }}
              message={`Lavandería "${lav.nombre}" está desactivada`}
              description="Esta propiedad apunta a una lavandería actualmente inactiva. Los lavados auto-generados de bookings nuevos seguirán contabilizándose para ella. Reasigna a otra lavandería activa si ya no la usas."
            />
          );
        }}
      </Form.Item>
    </div>
  );
};

export default PropiedadFinancePanel;
